import { useState, useEffect } from 'react';
import { Lightbulb, Loader2, Sparkles } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';

interface NarrativeSummaryProps {
  tokenSymbol: string;
  tokenName: string;
  priceChange24h?: number;
  priceChange7d?: number;
  narrative?: string;
  buySignals?: Array<{ type: string; description: string }>;
  compact?: boolean;
}

interface NarrativeBullet {
  icon: string;
  text: string;
  category: 'catalyst' | 'technical' | 'social' | 'fundamental';
}

export const NarrativeSummary = ({ 
  tokenSymbol, 
  tokenName, 
  priceChange24h,
  priceChange7d,
  narrative,
  buySignals,
  compact = false
}: NarrativeSummaryProps) => {
  const [bullets, setBullets] = useState<NarrativeBullet[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);

  // Generate bullets from available data (no API call needed)
  const generateBulletsFromData = (): NarrativeBullet[] => {
    const generatedBullets: NarrativeBullet[] = [];
    
    // 1. Price momentum bullet
    if (priceChange24h !== undefined && priceChange7d !== undefined) {
      if (priceChange24h > 10 && priceChange7d > 20) {
        generatedBullets.push({
          icon: '🚀',
          text: `Strong momentum: +${priceChange24h.toFixed(1)}% (24h), +${priceChange7d.toFixed(1)}% (7d)`,
          category: 'technical'
        });
      } else if (priceChange24h > 5) {
        generatedBullets.push({
          icon: '📈',
          text: `Bullish price action: +${priceChange24h.toFixed(1)}% in 24h`,
          category: 'technical'
        });
      } else if (priceChange24h < -10 && priceChange7d > 0) {
        generatedBullets.push({
          icon: '🎯',
          text: `Potential dip buy: -${Math.abs(priceChange24h).toFixed(1)}% today but +${priceChange7d.toFixed(1)}% weekly`,
          category: 'technical'
        });
      }
    }

    // 2. Narrative/sector bullet
    if (narrative) {
      const narrativeMap: Record<string, string> = {
        'AI & Machine Learning': '🤖 AI narrative heating up - sector outperforming market',
        'Layer 2 Scaling': '⚡ L2 momentum building as Ethereum scales',
        'DeFi Innovation': '💎 DeFi renaissance - yields and TVL rising',
        'Gaming & Metaverse': '🎮 Gaming sector showing renewed interest',
        'Meme Coin': '🐸 Meme momentum - high risk, high reward play',
        'Infrastructure': '🏗️ Infrastructure play - building Web3 backbone',
        'Altcoin': '💰 Altcoin season indicator active'
      };
      
      const narrativeText = narrativeMap[narrative] || `📊 ${narrative} - trending sector`;
      generatedBullets.push({
        icon: narrativeText.slice(0, 2),
        text: narrativeText.slice(3),
        category: 'fundamental'
      });
    }

    // 3. Buy signals bullet
    if (buySignals && buySignals.length > 0) {
      // Prioritize certain signals
      const socialSignal = buySignals.find(s => s.type === 'SOCIAL_MOMENTUM');
      const volumeSignal = buySignals.find(s => s.type === 'VOLUME');
      const momentumSignal = buySignals.find(s => s.type === 'MOMENTUM' || s.type === 'PUMP');
      const unlockWarning = buySignals.find(s => s.type === 'UNLOCK_WARNING');
      const supplyHealthSignal = buySignals.find(s => s.type === 'Supply Health');
      const athRecoverySignal = buySignals.find(s => s.type === 'ATH Recovery Signal');
      const supplyCirculationWatch = buySignals.find(s => s.type === 'Supply Circulation Watch');

      if (supplyHealthSignal) {
        generatedBullets.push({
          icon: '🧮',
          text: supplyHealthSignal.description.replace(/^Thinking point:\s*/, ''),
          category: 'fundamental'
        });
      } else if (athRecoverySignal) {
        generatedBullets.push({
          icon: '📉',
          text: athRecoverySignal.description.replace(/^Thinking point:\s*/, ''),
          category: 'technical'
        });
      } else if (supplyCirculationWatch) {
        generatedBullets.push({
          icon: '⚖️',
          text: supplyCirculationWatch.description.replace(/^Thinking point:\s*/, ''),
          category: 'fundamental'
        });
      } else if (socialSignal) {
        generatedBullets.push({
          icon: '📣',
          text: socialSignal.description.replace(/^\[.*?\]\s*/, ''),
          category: 'social'
        });
      } else if (volumeSignal) {
        generatedBullets.push({
          icon: '📊',
          text: volumeSignal.description,
          category: 'technical'
        });
      } else if (momentumSignal) {
        generatedBullets.push({
          icon: '⚡',
          text: momentumSignal.description,
          category: 'technical'
        });
      }

      if (unlockWarning) {
        generatedBullets.push({
          icon: '⚠️',
          text: unlockWarning.description.replace(/^⚠️\s*/, ''),
          category: 'fundamental'
        });
      }
    }

    // 4. Add a generic catalyst if we have fewer than 3 bullets
    if (generatedBullets.length < 3) {
      const catalysts = [
        { icon: '🔜', text: 'Watch for upcoming announcements and partnerships', category: 'catalyst' as const },
        { icon: '📅', text: 'Check roadmap for near-term deliverables', category: 'catalyst' as const },
        { icon: '👥', text: 'Community growth indicates rising interest', category: 'social' as const }
      ];
      
      while (generatedBullets.length < 3 && catalysts.length > 0) {
        const catalyst = catalysts.shift();
        if (catalyst && !generatedBullets.find(b => b.text === catalyst.text)) {
          generatedBullets.push(catalyst);
        }
      }
    }

    return generatedBullets.slice(0, 3);
  };

  useEffect(() => {
    if (!hasGenerated) {
      const generated = generateBulletsFromData();
      setBullets(generated);
      setHasGenerated(true);
    }
  }, [tokenSymbol, priceChange24h, priceChange7d, narrative, buySignals, hasGenerated]);

  const getCategoryColor = (category: NarrativeBullet['category']) => {
    switch (category) {
      case 'catalyst': return 'text-yellow-400';
      case 'technical': return 'text-blue-400';
      case 'social': return 'text-purple-400';
      case 'fundamental': return 'text-green-400';
      default: return 'text-muted-foreground';
    }
  };

  if (bullets.length === 0) {
    return null;
  }

  if (compact) {
    return (
      <div className="space-y-1">
        {bullets.map((bullet, idx) => (
          <div key={idx} className="flex items-start gap-2 text-xs">
            <span>{bullet.icon}</span>
            <span className={getCategoryColor(bullet.category)}>{bullet.text}</span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
        <Lightbulb className="h-4 w-4 text-yellow-400" />
        Why {tokenSymbol}?
      </div>
      <div className="space-y-2 pl-1">
        {bullets.map((bullet, idx) => (
          <div 
            key={idx} 
            className="flex items-start gap-2 text-sm"
          >
            <span className="text-base leading-none mt-0.5">{bullet.icon}</span>
            <span className={`${getCategoryColor(bullet.category)}`}>
              {bullet.text}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};
